import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Download, ExternalLink, ChevronLeft, ChevronRight, HelpCircle } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Sidebar from '../components/Sidebar';
import TopHeaderBar from '../components/TopHeaderBar';

const MERCOR_PURPLE = '#5B3DC5';

/* Chart Data */
const generateChartData = () => [
  { name: 'Feb 1 - Feb 6, 2026', paidEarnings: 0, pendingEarnings: 0 },
  { name: 'Feb 7 - Feb 13, 2026', paidEarnings: 0, pendingEarnings: 0 },
  { name: 'Feb 14 - Feb 20, 2026', paidEarnings: 0, pendingEarnings: 0 },
  { name: 'Feb 21 - Feb 23, 2026', paidEarnings: 0, pendingEarnings: 0 },
];

const generateHoursData = () => [
  { name: 'Feb 1', hoursWorked: 0 },
  { name: 'Feb 7 - Feb 13, 2026', hoursWorked: 0 },
  { name: 'Feb 14 - Feb 20, 2026', hoursWorked: 0 },
  { name: 'Feb 21', hoursWorked: 0 },
];

/* Countries */
const COUNTRIES = [
  'Albania','Algeria','Angola','Antigua & Barbuda','Argentina','Armenia','Australia','Austria','Bangladesh','Belgium','Brazil',
  'Canada','Chile','China','Colombia','Denmark','Egypt','Finland','France','Germany','Greece',
  'India','Indonesia','Ireland','Israel','Italy','Japan','Kenya','Malaysia','Mexico','Netherlands',
  'New Zealand','Nigeria','Norway','Pakistan','Philippines','Poland','Portugal','Singapore',
  'South Africa','South Korea','Spain','Sweden','Switzerland','Taiwan','Thailand','Turkey',
  'UAE','United Kingdom','United States','Vietnam',
];

const TYPE_OPTIONS = [
  { id: 'contract', label: 'Contract', sub: 'Contract earnings', icon: '📄' },
  { id: 'incentive', label: 'Incentive', sub: 'Incentive payments', icon: '💰' },
  { id: 'work-trial', label: 'Work Trial', sub: 'Trial period payments', icon: '🔄' },
  { id: 'referral', label: 'Referral', sub: 'Referral bonuses', icon: '🔗' },
];

/* ═══ Payment Setup Modal ═══ */
const PaymentSetupModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState('country');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white shadow-2xl" style={{ borderRadius: '12px', width: '100%', maxWidth: '460px', margin: '0 16px' }}>
        <div style={{ padding: '28px 32px 32px' }}>
          {step === 'country' ? (
            <>
              <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#1A1A1A', marginBottom: '6px' }}>Setup your Payment Method</h2>
              <p style={{ fontSize: '14px', color: '#6B7280', marginBottom: '20px' }}>Select the country in which you live and work</p>
              <div className="relative" style={{ marginBottom: '20px' }}>
                <button onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="w-full flex items-center justify-between"
                  style={{ padding: '10px 14px', fontSize: '14px', border: '1px solid #E5E7EB', borderRadius: '8px', backgroundColor: '#FFFFFF' }}>
                  <span style={{ color: selectedCountry ? '#1A1A1A' : '#9CA3AF' }}>{selectedCountry || 'Select your country'}</span>
                  <ChevronDown style={{ width: '16px', height: '16px', color: '#9CA3AF' }} />
                </button>
                {dropdownOpen && (
                  <div className="absolute top-full left-0 right-0 bg-white shadow-lg z-10"
                    style={{ marginTop: '4px', border: '1px solid #E5E7EB', borderRadius: '8px', maxHeight: '200px', overflowY: 'auto' }}>
                    {COUNTRIES.map(c => (
                      <button key={c} onClick={() => { setSelectedCountry(c); setDropdownOpen(false); }}
                        className="w-full text-left hover:bg-gray-50"
                        style={{ padding: '8px 14px', fontSize: '14px', color: '#374151' }}>{c}</button>
                    ))}
                  </div>
                )}
              </div>
              <button onClick={() => selectedCountry && setStep('stripe')} disabled={!selectedCountry}
                className="w-full font-semibold text-white transition-opacity hover:opacity-90"
                style={{ padding: '12px 0', borderRadius: '10px', fontSize: '14px', backgroundColor: selectedCountry ? MERCOR_PURPLE : '#D1D5DB' }}>
                Next
              </button>
            </>
          ) : (
            <>
              <button onClick={() => setStep('country')} className="flex items-center hover:opacity-70"
                style={{ gap: '4px', fontSize: '14px', color: '#6B7280', marginBottom: '16px' }}>
                <ChevronLeft style={{ width: '16px', height: '16px' }} /> Back
              </button>
              <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#1A1A1A', marginBottom: '6px' }}>Creating a Stripe account</h2>
              <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '20px', lineHeight: '1.6' }}>
                We use Stripe to process payouts. Please write the exact details below when creating your Stripe account, or the page which says "tell us about the business you represent."
              </p>
              <div style={{ marginBottom: '24px' }}>
                <div style={{ marginBottom: '16px' }}>
                  <p style={{ fontSize: '14px', fontWeight: 600, color: '#1A1A1A', marginBottom: '4px' }}>
                    Type of business: <span style={{ color: MERCOR_PURPLE }}>Individual</span>
                  </p>
                  <p style={{ fontSize: '12px', color: '#6B7280', lineHeight: '1.5' }}>Please do not register as a business unless you have a formal independent entity created in the state of your business' origin.</p>
                </div>
                <div>
                  <p style={{ fontSize: '14px', fontWeight: 600, color: '#1A1A1A', marginBottom: '4px' }}>
                    Country: <span style={{ color: MERCOR_PURPLE }}>Where you live and work</span>
                  </p>
                  <p style={{ fontSize: '12px', color: '#6B7280', lineHeight: '1.5' }}>Please ensure you select the country where they live and where their work is performed. If the user working from a different country than the one they're working, you may also select a different country.</p>
                </div>
              </div>
              <div className="flex items-center" style={{ gap: '12px' }}>
                <button onClick={() => setStep('country')}
                  className="flex-1 font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  style={{ padding: '10px 0', fontSize: '13px', border: '1px solid #D1D5DB', borderRadius: '10px' }}>
                  Reset payment setup
                </button>
                <button onClick={onClose}
                  className="flex-1 font-semibold text-white hover:opacity-90 transition-opacity"
                  style={{ padding: '10px 0', fontSize: '13px', backgroundColor: MERCOR_PURPLE, borderRadius: '10px' }}>
                  Continue to Stripe setup
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/* ═══ Download Report Modal ═══ */
const DownloadReportModal = ({ isOpen, onClose }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date(2026, 1));
  if (!isOpen) return null;

  const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
  const firstDayOfWeek = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay();
  const nextMonthDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1);
  const nextMonthDays = new Date(nextMonthDate.getFullYear(), nextMonthDate.getMonth() + 1, 0).getDate();
  const nextMonthFirstDay = new Date(nextMonthDate.getFullYear(), nextMonthDate.getMonth(), 1).getDay();

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white shadow-2xl" style={{ borderRadius: '12px', width: '100%', maxWidth: '620px', margin: '0 16px' }}>
        <div style={{ padding: '28px 32px 28px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 700, color: '#1A1A1A', marginBottom: '4px' }}>Download Payment Report</h2>
          <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '24px' }}>Choose a date range (you can get the last 2 years of data at most).</p>
          <div className="grid grid-cols-2" style={{ gap: '32px', marginBottom: '20px' }}>
            {/* Month 1 */}
            <div>
              <div className="flex items-center justify-between" style={{ marginBottom: '12px' }}>
                <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))} className="hover:bg-gray-100 rounded" style={{ padding: '4px' }}>
                  <ChevronLeft style={{ width: '16px', height: '16px', color: '#6B7280' }} />
                </button>
                <span style={{ fontSize: '14px', fontWeight: 600, color: '#1A1A1A' }}>{monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}</span>
                <span style={{ width: '24px' }} />
              </div>
              <div className="grid grid-cols-7 text-center" style={{ gap: '2px' }}>
                {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => <div key={d} style={{ fontSize: '11px', fontWeight: 500, color: '#9CA3AF', padding: '4px 0' }}>{d}</div>)}
                {Array.from({ length: firstDayOfWeek }).map((_, i) => <div key={`e${i}`} />)}
                {Array.from({ length: daysInMonth }).map((_, i) => (
                  <button key={i} className="mx-auto rounded-full flex items-center justify-center text-white hover:opacity-90"
                    style={{ width: '32px', height: '32px', fontSize: '12px', fontWeight: 500, backgroundColor: MERCOR_PURPLE }}>{i + 1}</button>
                ))}
              </div>
            </div>
            {/* Month 2 */}
            <div>
              <div className="flex items-center justify-between" style={{ marginBottom: '12px' }}>
                <span style={{ width: '24px' }} />
                <span style={{ fontSize: '14px', fontWeight: 600, color: '#1A1A1A' }}>{monthNames[nextMonthDate.getMonth()]} {nextMonthDate.getFullYear()}</span>
                <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))} className="hover:bg-gray-100 rounded" style={{ padding: '4px' }}>
                  <ChevronRight style={{ width: '16px', height: '16px', color: '#6B7280' }} />
                </button>
              </div>
              <div className="grid grid-cols-7 text-center" style={{ gap: '2px' }}>
                {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => <div key={d} style={{ fontSize: '11px', fontWeight: 500, color: '#9CA3AF', padding: '4px 0' }}>{d}</div>)}
                {Array.from({ length: nextMonthFirstDay }).map((_, i) => <div key={`e${i}`} />)}
                {Array.from({ length: nextMonthDays }).map((_, i) => (
                  <button key={i} className="mx-auto rounded-full flex items-center justify-center hover:bg-gray-100"
                    style={{ width: '32px', height: '32px', fontSize: '12px', fontWeight: 500, color: '#374151' }}>{i + 1}</button>
                ))}
              </div>
            </div>
          </div>
          <p style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '16px' }}>You will receive the report via email.</p>
          <div className="flex items-center justify-end" style={{ gap: '12px' }}>
            <button onClick={onClose} className="font-medium text-gray-600 hover:bg-gray-50 transition-colors"
              style={{ padding: '8px 20px', fontSize: '13px', border: '1px solid #D1D5DB', borderRadius: '8px' }}>Cancel</button>
            <button className="font-semibold text-white hover:opacity-90 transition-opacity"
              style={{ padding: '8px 20px', fontSize: '13px', backgroundColor: MERCOR_PURPLE, borderRadius: '8px' }}>Request Download Report</button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ═══ Main Earnings Page ═══ */
const MercorEarningsPage = () => {
  const navigate = useNavigate();
  const [paymentSetupOpen, setPaymentSetupOpen] = useState(false);
  const [downloadReportOpen, setDownloadReportOpen] = useState(false);
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
  const [contractsDropdownOpen, setContractsDropdownOpen] = useState(false);

  const earningsData = useMemo(() => generateChartData(), []);
  const hoursData = useMemo(() => generateHoursData(), []);

  /* Filter pill matching Mercor exactly */
  const FilterPill = ({ label, isActive = false, onClick }) => (
    <button
      onClick={onClick}
      className="flex items-center whitespace-nowrap"
      style={{
        padding: '6px 12px',
        fontSize: '13px',
        fontWeight: 500,
        borderRadius: '6px',
        border: isActive ? `1.5px solid ${MERCOR_PURPLE}` : '1px solid #E5E7EB',
        backgroundColor: isActive ? '#F5F3FF' : '#FFFFFF',
        color: isActive ? MERCOR_PURPLE : '#4B5563',
        gap: '6px',
      }}
    >
      {label}
      <ChevronDown style={{ width: '14px', height: '14px' }} />
    </button>
  );

  return (
    <div className="flex min-h-screen" style={{ backgroundColor: '#FFFFFF' }}>
      <Sidebar />
      <div className="flex-1 flex flex-col" style={{ marginLeft: '0px', paddingTop: '56px', paddingBottom: '56px' }}>
        {/* Desktop: shift content for sidebar */}
        <style>{`@media(min-width:768px){.earnings-main{margin-left:60px!important;padding-top:0!important;padding-bottom:0!important}}`}</style>
        <div className="earnings-main flex-1 flex flex-col" style={{ marginLeft: '0px', paddingTop: '56px', paddingBottom: '56px' }}>
          <TopHeaderBar />
          <div className="flex-1 overflow-y-auto">
            <div style={{ maxWidth: '960px', margin: '0 auto', padding: '32px 24px' }}>

              {/* ─── Header row ─── */}
              <div className="flex flex-col sm:flex-row sm:items-start justify-between" style={{ marginBottom: '8px' }}>
                <div>
                  <h1 style={{ fontSize: '26px', fontWeight: 700, color: '#1A1A1A', marginBottom: '6px', lineHeight: '1.2' }}>Earnings</h1>
                  <p style={{ fontSize: '14px', color: '#6B7280' }}>
                    Your total earnings to date are <span style={{ fontWeight: 600, color: '#1A1A1A' }}>$0.00</span>.{' '}
                    <span onClick={() => navigate('/explore')} className="cursor-pointer hover:opacity-80"
                      style={{ color: MERCOR_PURPLE, textDecoration: 'underline', textUnderlineOffset: '2px' }}>
                      Explore open roles →
                    </span>
                  </p>
                </div>
                <div className="flex items-center shrink-0" style={{ gap: '12px', marginTop: '4px' }}>
                  <span className="hidden sm:inline" style={{ fontSize: '13px', color: '#9CA3AF' }}>No payment method connected</span>
                  <button onClick={() => setPaymentSetupOpen(true)}
                    className="font-semibold text-white hover:opacity-90 transition-opacity"
                    style={{ padding: '8px 20px', fontSize: '13px', backgroundColor: MERCOR_PURPLE, borderRadius: '8px' }}>
                    Connect provider
                  </button>
                </div>
              </div>

              {/* ─── Unlock banner ─── */}
              <div style={{ borderRadius: '10px', padding: '16px 20px', marginBottom: '32px', marginTop: '16px', backgroundColor: '#F5F3FF', border: '1px solid #E9D5FF' }}>
                <p style={{ fontSize: '14px', color: '#374151', marginBottom: '12px', lineHeight: '1.5' }}>
                  <span style={{ fontWeight: 600 }}>Unlock more earnings with warm intros.</span>{' '}
                  Refer talented friends and colleagues, and Mercor will reward you when they get hired and proceed to working presently.
                </p>
                <button className="flex items-center font-semibold text-white hover:opacity-90 transition-opacity"
                  style={{ padding: '8px 16px', fontSize: '13px', backgroundColor: MERCOR_PURPLE, borderRadius: '8px', gap: '6px' }}>
                  Explore LinkedIn Connections
                  <ExternalLink style={{ width: '14px', height: '14px' }} />
                </button>
              </div>

              {/* ─── Earnings Over Time ─── */}
              <div style={{ marginBottom: '40px' }}>
                <h2 style={{ fontSize: '17px', fontWeight: 700, color: '#1A1A1A', marginBottom: '16px' }}>Earnings Over Time</h2>
                <div className="flex items-center flex-wrap" style={{ gap: '8px', marginBottom: '16px' }}>
                  <FilterPill label="Billing Date: Feb 1 - Feb 23, 2026" isActive />
                  <div className="relative">
                    <FilterPill label="Contracts" onClick={() => setContractsDropdownOpen(!contractsDropdownOpen)} />
                    {contractsDropdownOpen && (
                      <div className="absolute top-full left-0 bg-white shadow-lg z-10"
                        style={{ marginTop: '4px', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '12px', width: '220px' }}>
                        <p style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '8px' }}>No active contracts</p>
                        <button onClick={() => setContractsDropdownOpen(false)} style={{ fontSize: '12px', color: MERCOR_PURPLE }}>Close</button>
                      </div>
                    )}
                  </div>
                  <div className="relative">
                    <FilterPill label="Type" onClick={() => setTypeDropdownOpen(!typeDropdownOpen)} />
                    {typeDropdownOpen && (
                      <div className="absolute top-full left-0 bg-white shadow-lg z-10"
                        style={{ marginTop: '4px', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '8px', width: '280px' }}>
                        {TYPE_OPTIONS.map(opt => (
                          <label key={opt.id} className="flex items-start cursor-pointer hover:bg-gray-50 rounded-lg"
                            style={{ gap: '10px', padding: '8px 10px' }}>
                            <input type="checkbox" className="rounded border-gray-300" style={{ accentColor: MERCOR_PURPLE, marginTop: '2px' }} />
                            <div>
                              <div className="flex items-center" style={{ gap: '6px' }}>
                                <span style={{ fontSize: '13px' }}>{opt.icon}</span>
                                <span style={{ fontSize: '13px', fontWeight: 600, color: '#1A1A1A' }}>{opt.label}</span>
                              </div>
                              <p style={{ fontSize: '12px', color: '#9CA3AF' }}>{opt.sub}</p>
                            </div>
                          </label>
                        ))}
                        <button onClick={() => setTypeDropdownOpen(false)} className="w-full text-center"
                          style={{ fontSize: '12px', color: MERCOR_PURPLE, padding: '6px 0', marginTop: '4px' }}>Done</button>
                      </div>
                    )}
                  </div>
                  <div className="flex-1" />
                  <div className="flex items-center" style={{ gap: '8px' }}>
                    <span style={{ fontSize: '16px', color: '#9CA3AF' }}>$</span>
                    <svg style={{ width: '16px', height: '16px', color: '#9CA3AF', cursor: 'pointer' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 4v6h6" /><path d="M23 20v-6h-6" /><path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" /></svg>
                  </div>
                  <div className="text-right">
                    <p style={{ fontSize: '11px', color: '#9CA3AF' }}>Earnings total</p>
                    <p style={{ fontSize: '22px', fontWeight: 700, color: '#1A1A1A' }}>$0.00</p>
                  </div>
                </div>

                <div style={{ height: '200px', width: '100%' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={earningsData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={{ stroke: '#E5E7EB' }} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} tickFormatter={(v) => `$${v}`} axisLine={{ stroke: '#E5E7EB' }} tickLine={false} />
                      <Tooltip />
                      <Area type="monotone" dataKey="paidEarnings" stackId="1" stroke={MERCOR_PURPLE} fill="#C4B5FD" name="Paid Earnings" />
                      <Area type="monotone" dataKey="pendingEarnings" stackId="1" stroke="#A78BFA" fill="#EDE9FE" name="Pending Earnings" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex items-center" style={{ marginTop: '8px', gap: '20px' }}>
                  <div className="flex items-center" style={{ gap: '6px' }}>
                    <div style={{ width: '12px', height: '12px', borderRadius: '2px', backgroundColor: '#C4B5FD' }} />
                    <span style={{ fontSize: '12px', color: '#6B7280' }}>Paid Earnings</span>
                  </div>
                  <div className="flex items-center" style={{ gap: '6px' }}>
                    <div style={{ width: '12px', height: '12px', borderRadius: '2px', backgroundColor: '#EDE9FE' }} />
                    <span style={{ fontSize: '12px', color: '#6B7280' }}>Pending Earnings</span>
                  </div>
                  <div className="flex-1" />
                  <span style={{ fontSize: '11px', color: '#9CA3AF' }}>Data refreshes every hour</span>
                </div>
              </div>

              {/* ─── Hours Over Time ─── */}
              <div style={{ marginBottom: '40px' }}>
                <div className="flex items-center justify-between" style={{ marginBottom: '16px' }}>
                  <h2 style={{ fontSize: '17px', fontWeight: 700, color: '#1A1A1A' }}>Hours Over Time</h2>
                  <div className="flex items-center" style={{ gap: '8px' }}>
                    <svg style={{ width: '16px', height: '16px', color: '#9CA3AF' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                    <div className="text-right">
                      <p style={{ fontSize: '11px', color: '#9CA3AF' }}>Hours worked</p>
                      <p style={{ fontSize: '22px', fontWeight: 700, color: '#1A1A1A' }}>0.00</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center flex-wrap" style={{ gap: '8px', marginBottom: '16px' }}>
                  <FilterPill label="Billing Date: Feb 1 - Feb 23, 2026" isActive />
                  <FilterPill label="Contracts" />
                  <FilterPill label="Type" />
                </div>
                <div style={{ height: '180px', width: '100%' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={hoursData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={{ stroke: '#E5E7EB' }} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={{ stroke: '#E5E7EB' }} tickLine={false} />
                      <Tooltip />
                      <Area type="monotone" dataKey="hoursWorked" stroke="#94A3B8" fill="#F1F5F9" name="Hours Worked" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex items-center" style={{ marginTop: '8px', gap: '20px' }}>
                  <div className="flex items-center" style={{ gap: '6px' }}>
                    <div style={{ width: '12px', height: '12px', borderRadius: '2px', backgroundColor: '#94A3B8' }} />
                    <span style={{ fontSize: '12px', color: '#6B7280' }}>Hours Worked</span>
                  </div>
                  <div className="flex-1" />
                  <span style={{ fontSize: '11px', color: '#9CA3AF' }}>Data refreshes every hour</span>
                </div>
              </div>

              {/* ─── Payments Table ─── */}
              <div style={{ marginBottom: '32px' }}>
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between" style={{ marginBottom: '16px', gap: '12px' }}>
                  <h2 style={{ fontSize: '17px', fontWeight: 700, color: '#1A1A1A' }}>Payments</h2>
                  <button onClick={() => setDownloadReportOpen(true)}
                    className="flex items-center font-semibold text-white hover:opacity-90 transition-opacity"
                    style={{ padding: '8px 16px', fontSize: '13px', backgroundColor: MERCOR_PURPLE, borderRadius: '8px', gap: '6px' }}>
                    <Download style={{ width: '14px', height: '14px' }} />
                    Download Payment Report
                  </button>
                </div>
                <div style={{ border: '1px solid #E5E7EB', borderRadius: '10px', overflow: 'hidden' }}>
                  <table className="w-full">
                    <thead>
                      <tr style={{ backgroundColor: '#FAFAFA' }}>
                        {['Payout date','Type','Description','Status','Hours','Earned'].map((h, i) => (
                          <th key={h} style={{
                            padding: '12px 20px',
                            fontSize: '12px',
                            fontWeight: 500,
                            color: '#6B7280',
                            textAlign: i >= 4 ? 'right' : 'left',
                            borderBottom: '1px solid #E5E7EB',
                          }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td colSpan="6" style={{ padding: '48px 20px', textAlign: 'center' }}>
                          <p style={{ fontSize: '14px', color: '#6B7280', fontWeight: 500 }}>No payment history yet</p>
                          <p style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '4px' }}>Once you start working, your earnings will appear here</p>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* Floating chat button - exact Mercor position */}
      <button className="fixed flex items-center justify-center text-white rounded-full shadow-lg hover:opacity-90 z-50"
        style={{ bottom: '24px', right: '24px', width: '48px', height: '48px', backgroundColor: MERCOR_PURPLE }}>
        <HelpCircle style={{ width: '24px', height: '24px' }} />
      </button>

      <PaymentSetupModal isOpen={paymentSetupOpen} onClose={() => setPaymentSetupOpen(false)} />
      <DownloadReportModal isOpen={downloadReportOpen} onClose={() => setDownloadReportOpen(false)} />
    </div>
  );
};

export default MercorEarningsPage;
